import axios from "axios";
import { api } from "./apiClient";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_SESSION_KEY = "ff_cart_session";

/**
 * Cart identity is fragile across browsers because the backend session cookie
 * is HttpOnly, cross-origin, and gets stripped by some browser privacy modes.
 *
 * We keep a mirror of the session token in localStorage and send it as
 * `X-Cart-Session` on every cart call. The backend accepts the header first
 * and falls back to the cookie; every response echoes back
 * `X-Cart-Session` (also present in the JSON body as `session_token`) so the
 * client can re-persist it. This is what actually fixes the "Your cart is
 * empty" bug — the browser was silently dropping the cookie between the
 * add-to-cart POST and the /cart GET on the next page load.
 */
function getCartToken() {
  try { return localStorage.getItem(CART_SESSION_KEY) || ""; } catch { return ""; }
}
function setCartToken(t) {
  try { if (t) localStorage.setItem(CART_SESSION_KEY, t); } catch { /* private mode */ }
}
export function clearCartToken() {
  try { localStorage.removeItem(CART_SESSION_KEY); } catch { /* noop */ }
}

const publicClient = axios.create({ baseURL: BASE, withCredentials: true });

publicClient.interceptors.request.use((cfg) => {
  const t = getCartToken();
  if (t) cfg.headers["X-Cart-Session"] = t;
  return cfg;
});

publicClient.interceptors.response.use((res) => {
  const hdr = res.headers?.["x-cart-session"] || res.headers?.["X-Cart-Session"];
  const body = res.data?.session_token;
  const tok = hdr || body;
  if (tok) setCartToken(tok);
  return res;
});

export const cartApi = {
  get: () => publicClient.get("/cart").then((r) => r.data),
  add: (product_id, qty = 1) =>
    publicClient.post("/cart/items", { product_id, qty }).then((r) => r.data),
  update: (product_id, qty) =>
    publicClient.patch(`/cart/items/${product_id}`, { qty }).then((r) => r.data),
  remove: (product_id) =>
    publicClient.delete(`/cart/items/${product_id}`).then((r) => r.data),
  checkout: (body, idempotencyKey) =>
    publicClient
      .post("/checkout", body, { headers: { "Idempotency-Key": idempotencyKey } })
      .then((r) => r.data),
  order: (order_number) =>
    publicClient.get(`/orders/${order_number}`).then((r) => r.data),
};

export const adminProductsApi = {
  categories: () => api.get("/admin/categories").then((r) => r.data),
  createCategory: (b) => api.post("/admin/categories", b).then((r) => r.data),
  updateCategory: (id, b) => api.put(`/admin/categories/${id}`, b).then((r) => r.data),
  archiveCategory: (id) => api.delete(`/admin/categories/${id}`).then((r) => r.data),
  list: (params = {}) => api.get("/admin/products", { params }).then((r) => r.data),
  get: (id) => api.get(`/admin/products/${id}`).then((r) => r.data),
  create: (b) => api.post("/admin/products", b).then((r) => r.data),
  update: (id, b) => api.put(`/admin/products/${id}`, b).then((r) => r.data),
  // Backend chooses hard-delete or archive automatically for safety.
  remove: (id) => api.delete(`/admin/products/${id}`).then((r) => r.data),
  archive: (id) => api.delete(`/admin/products/${id}`).then((r) => r.data),
  bulkDelete: (ids) => api.post("/admin/products/bulk-delete", { ids }).then((r) => r.data),
  uploadImage: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/admin/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data);
  },
  importPreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/admin/products/import", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  importConfirm: (id) => api.post(`/admin/products/import/${id}/confirm`).then((r) => r.data),
};

export const inquiriesApi = {
  // Public — best-effort; never throws.
  log: (payload) =>
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/inquiries`, payload)
      .catch(() => null),
};

export const adminOrdersApi = {
  list: (params = {}) => api.get("/admin/orders", { params }).then((r) => r.data),
  get: (id) => api.get(`/admin/orders/${id}`).then((r) => r.data),
  verify: (id) => api.post(`/admin/orders/${id}/verify`).then((r) => r.data),
  reject: (id, reason) => api.post(`/admin/orders/${id}/reject`, { reason }).then((r) => r.data),
  transition: (id, to, note) => api.post(`/admin/orders/${id}/transition`, { to, note }).then((r) => r.data),
  assignDelivery: (id, driver_id, note) =>
    api.post(`/admin/orders/${id}/assign-delivery`, { driver_id, note }).then((r) => r.data),
  confirmPayment: (id, body = {}) =>
    api.post(`/admin/orders/${id}/confirm-payment`, body).then((r) => r.data),
};

export const adminDriversApi = {
  list: () => api.get("/admin/drivers").then((r) => r.data),
  create: (b) => api.post("/admin/drivers", b).then((r) => r.data),
  update: (id, b) => api.put(`/admin/drivers/${id}`, b).then((r) => r.data),
  deactivate: (id) => api.delete(`/admin/drivers/${id}`).then((r) => r.data),
};

export const adminInvoicesApi = {
  list: (params = {}) => api.get("/admin/invoices", { params }).then((r) => r.data),
  get: (id) => api.get(`/admin/invoices/${id}`).then((r) => r.data),
  createFromOrder: (orderId) =>
    api.post(`/admin/invoices/from-order/${orderId}`).then((r) => r.data),
  downloadPdf: (id) =>
    api
      .get(`/admin/invoices/${id}/pdf`, { responseType: "blob" })
      .then((r) => r.data),
};

export const adminInquiriesApi = {
  list: (params = {}) => api.get("/admin/inquiries", { params }).then((r) => r.data),
  setStatus: (id, status) => api.patch(`/admin/inquiries/${id}`, { status }).then((r) => r.data),
  convertToOrder: (id) => api.post(`/admin/inquiries/${id}/convert-to-order`).then((r) => r.data),
};

export const customerMeApi = {
  orders: () => api.get("/customer/me/orders").then((r) => r.data),
  inquiries: () => api.get("/customer/me/inquiries").then((r) => r.data),
};

export const adminDashboardApi = {
  summary: () => api.get("/admin/dashboard/summary").then((r) => r.data),
};

export const adminSystemApi = {
  wipeOrders: () => api.post("/admin/system/wipe-orders", { confirm: "WIPE" }).then((r) => r.data),
};

export function newIdempotencyKey() {
  return `ff-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
